/**
 * The console's governed IANA timezone vocabulary.
 *
 * It is experience data shared by every timezone editor, not a control-local
 * literal. Values are IANA identifiers accepted by Intl; labels deliberately
 * preserve those identifiers so storage and presentation cannot disagree.
 */
export const GOVERNED_TIMEZONES = [
  'Europe/Brussels', 'Europe/London', 'Europe/Amsterdam', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Asia/Dubai', 'Asia/Singapore', 'Australia/Sydney', 'UTC',
].map((zone) => ({ value: zone, label: zone }))

export function isIanaTimezone(zone: string): boolean {
  if (!zone.trim()) return false
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone }).format()
    return true
  } catch {
    return false
  }
}

/** Preference → browser zone → UTC, accepting only an Intl-recognised value. */
export function currentTimezone(preferred?: string | null, browserZone?: string | null): string {
  for (const candidate of [preferred, browserZone, 'UTC']) {
    if (candidate && isIanaTimezone(candidate)) return candidate
  }
  return 'UTC'
}

/**
 * Put the resolved current zone into the closed catalogue without admitting an
 * arbitrary string. The current Intl zone may be newer than this deployment's
 * curated list; that is still governed data, while an unrecognised value is
 * neither rendered nor submitted.
 */
export function timezoneChoices(
  current: string,
  catalogue: readonly { value: string; label: string }[] = GOVERNED_TIMEZONES,
): { value: string; label: string }[] {
  const seen = new Set<string>()
  return [
    ...(isIanaTimezone(current) ? [{ value: current, label: current }] : []),
    ...catalogue,
  ].filter((option) => {
    if (!isIanaTimezone(option.value) || seen.has(option.value)) return false
    seen.add(option.value)
    return true
  })
}
